import * as atcute from "@atcute/client";
import { At } from "@atcute/client/lexicons";
import { mergeHeaders } from "@atcute/client/utils/http";
import * as oauth from "npm:@atcute/oauth-browser-client@1";
import { BSKY_MODERATION, labelerServiceIDs } from "./state/labelers.ts";

oauth.configureOAuth({
  metadata: {
    client_id: import.meta.env.OAUTH_CLIENT_ID,
    redirect_uri: import.meta.env.OAUTH_REDIRECT_URL,
  },
});

export interface Session {
  session: oauth.Session;
  did: string;

  xrpc: atcute.XRPC;
}

const loadOAuthSession = async (): Promise<oauth.Session | undefined> => {
  const params = new URLSearchParams(location.hash.substring(1));
  if (params.has("state") && (params.has("code") || params.has("error"))) {
    history.replaceState(null, "", location.pathname + location.search);
    const session = await oauth.finalizeAuthorization(params);
    return session;
  }

  const lastUsedSession = localStorage.getItem("rainbow/last-used-session");
  if (lastUsedSession !== null) {
    try {
      return await oauth.getSession(lastUsedSession as At.DID);
    } catch {
      // ignore
    }
  }

  try {
    const did = oauth.listStoredSessions().at(0);
    if (did !== undefined) return await oauth.getSession(did);
  } catch {
    // ignore
  }

  return undefined;
};

export const loadSession = async (
  oauthSession: oauth.Session,
): Promise<Session | undefined> => {
  const did = oauthSession.info.sub;

  const agent = new oauth.OAuthUserAgent(oauthSession);
  const handler = async (path: string, init: RequestInit) => {
    init.headers = mergeHeaders(init.headers, {
      "atproto-accept-labelers": labelerServiceIDs
        .get()
        .map(it => (it === BSKY_MODERATION ? `${it};redact` : it)) // TODO: make this configurable
        .join(", "),
    });

    return await agent.handle(path, init);
  };

  const xrpc = new atcute.XRPC({ handler });

  return { did, session: oauthSession, xrpc };
};

export let session: Session | undefined;
export function setSession(session_: Session) {
  session = session_;
}

const oauthSession = await loadOAuthSession();
if (oauthSession !== undefined) session = await loadSession(oauthSession);

export function updateStoredSession() {
  if (session) localStorage.setItem("rainbow/last-used-session", session.did);
  else localStorage.removeItem("rainbow/last-used-session");
}
updateStoredSession();
